import { Injectable, Inject } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import { Observable, Subscription, empty } from 'rxjs';
import {
  map,
  filter,
  distinctUntilChanged,
  publishReplay,
  refCount,
  take,
} from 'rxjs/operators';
import {
  WebsocketGatewayService,
  EnvironmentConfig,
  ENVIRONMENT_CONFIG,
} from '@ipreo/northstar';
import { getFollowsStoreState } from '../state/reducer';
import {
  Load,
  GroupFollowUpdate,
  GroupUnfollowUpdate,
  Follow,
  Unfollow,
  GroupFollow,
  GroupUnfollow,
} from '../state/follows/actions';
import { PermissionsFacade } from './permissions.facade';
import {
  GroupFollowUpdateMessage,
  GroupFollowActionType,
} from '../types/live-update.types';
import { PlatformType } from '../types/muni-environment';

export interface CombinedFollows {
  loading: boolean;
  follows: string[];
  followsInProcess: string[];
  followSaving: boolean;
  groupFollows: string[];
  groupFollowsInProcess: string[];
  groupFollowSaving: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class FollowsFacade {
  public readonly data$: Observable<CombinedFollows>;

  private liveUpdateSubscription$: Subscription;

  constructor(
    private store: Store<Record<string, unknown>>,
    private permissionsFacade: PermissionsFacade,
    private wsgService: WebsocketGatewayService,
    @Inject(ENVIRONMENT_CONFIG) private config: EnvironmentConfig
  ) {
    if (config.params.platform === PlatformType.buyside) {
      this.data$ = this.trackLiveUpdates(
        store.pipe(
          select(getFollowsStoreState),
          map((storeState) => {
            if (storeState.error) {
              throw new Error(storeState.error);
            } else {
              return storeState;
            }
          }),
          filter((storeState) => storeState.loaded),
          map((storeState) => ({
            loading: storeState.loading,
            follows: storeState.dealIds,
            followsInProcess: storeState.dealIdsFollowInProcess,
            followSaving: storeState.dealIdsFollowInProcess.length > 0,
            groupFollows: storeState.groupFollowDealIds,
            groupFollowsInProcess: storeState.dealIdsGroupFollowInProcess,
            groupFollowSaving:
              storeState.dealIdsGroupFollowInProcess.length > 0,
          })),
          distinctUntilChanged(isEqual)
        )
      ).pipe(publishReplay(1), refCount());
    } else {
      this.data$ = empty();
    }
  }

  public toggleFollow(dealId: string) {
    this.withState(({ follows }) => {
      if (follows.includes(dealId)) {
        this.store.dispatch(new Unfollow(dealId));
      } else {
        this.store.dispatch(new Follow(dealId));
      }
    });
  }

  public toggleGroupFollow(dealId: string, notify = false) {
    this.withState(({ groupFollows }) => {
      if (groupFollows.includes(dealId)) {
        this.store.dispatch(new GroupUnfollow(dealId));
      } else {
        this.store.dispatch(new GroupFollow({ dealId, notify }));
      }
    });
  }

  private trackLiveUpdates<T>(source: Observable<T>) {
    return new Observable<T>((observer) => {
      // This is fired the first time someone subscribes to data$
      this.loadFollows();
      this.startLiveUpdateListener();

      const subscription = source.subscribe(observer);
      return () => {
        // This is fired when the last subscriber unsubscribes from data$
        subscription.unsubscribe();
        this.endLiveUpdateListener();
      };
    });
  }

  private loadFollows() {
    setTimeout(() => {
      this.store.dispatch(new Load());
    }, 1);
  }

  private startLiveUpdateListener() {
    if (!this.liveUpdateSubscription$) {
      console.log('Subscribing to Group Follow Live Updates');
      this.permissionsFacade.withPermission('groupFollowing').subscribe(() => {
        this.liveUpdateSubscription$ = this.wsgService
          .subscribe(this.config.params.groupFollowUpdateChannel as string)
          .subscribe((msg: GroupFollowUpdateMessage) => {
            switch (msg.action) {
              case GroupFollowActionType.groupFollow:
                this.store.dispatch(new GroupFollowUpdate(msg.dealId));
                break;
              case GroupFollowActionType.groupUnfollow:
                this.store.dispatch(new GroupUnfollowUpdate(msg.dealId));
                break;
              default:
                throw new Error(
                  `Unknown Group Follow WSG action: ${msg.action}`
                );
            }
          });
      });
    }
  }

  private endLiveUpdateListener() {
    if (this.liveUpdateSubscription$) {
      console.log('Unsubscribing from Group Follow Live Updates');
      this.liveUpdateSubscription$.unsubscribe();
      this.liveUpdateSubscription$ = null;
    }
  }

  private withState(callback: (CombinedFollows) => void) {
    this.data$.pipe(take(1)).subscribe(callback);
  }
}
